import { useSchedulerV3InvalidateQueries } from '../helper-hooks';
import { UpdateCalendarEventStatusIO } from '../types';
import { UseSchedulerV3MutationEndpointArgs, useSchedulerV3Mutation } from './use-scheduler-v3-mutation';

type MutationContext<C = unknown> = {
  // Add internal mutation context here if needed (useful for optimistic updates)
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `UpdateCalendarEventStatus` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useUpdateCalendarEventStatusMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends UpdateCalendarEventStatusIO['input'] = UpdateCalendarEventStatusIO['input']
>({
  options,
  optimisticUpdate = false,
  ...args
}: UseSchedulerV3MutationEndpointArgs<
  'UpdateCalendarEventStatus',
  E,
  C | undefined,
  OtherOptions,
  RequestOverride
> = {}) => {
  const { invalidateGetCalendarEvent } = useSchedulerV3InvalidateQueries();

  return useSchedulerV3Mutation<'UpdateCalendarEventStatus', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'UpdateCalendarEventStatus',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        if (optimisticUpdate) {
          // Implement optimistic updates here
        }

        return {
          // Add custom context type here (if exists in `MutationContext`)
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: async (response, request, context) => {
        // Implement query updates or invalidation for affected queries here
        invalidateGetCalendarEvent(response.event?.id);

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        // Revert optimistic update here (if implemented)

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
